import React, { ReactNode, useContext, useMemo } from 'react'
import { Context } from 'scala'
import { RepeatSong } from 'player/src/modules/repeat-song'
import { DataPlayer } from 'player'
import { AddTaskToPlaylistModal } from '../../../tasks/add-task-playlist-modal'
import { DeleteTask } from '../../../tasks/delete-task'
import { EditTask } from '../../../tasks/edit-task'
import { EmbedTask } from '../../../tasks/embed-task'
import { ViewInstructions } from '../../view-instructions'
import { Task } from '../../../../types'

interface UseSettingsActionsProps {
  task: Task
  data?: DataPlayer
}

export const useSettingsActions = ({
  data,
  task
}: UseSettingsActionsProps): ReactNode[] => {
  const { id, isOwner, isDemo, isPublic } = task
  const { user } = useContext(Context)

  const addToPlaylistNode = useMemo(
    () => isOwner && <AddTaskToPlaylistModal tracksIds={[id]} />,
    [id, isOwner]
  )

  const editTaskNode = useMemo(
    () =>
      !isDemo && (
        <EditTask
          taskId={id}
          isOwner={isOwner}
          summary={{
            ...task?.summary?.v1?.original,
            title: task?.summary?.v1?.original?.title || task?.file?.name,
            owner: task.owner
          }}
        />
      ),
    [id, isDemo, isOwner, task]
  )

  const repeatSongNode = useMemo(() => <RepeatSong />, [])

  const viewInstructionsNode = useMemo(
    () =>
      data?.posts ? (
        <ViewInstructions taskId={task.id} posts={data?.posts} />
      ) : null,
    [data?.posts, task?.id]
  )

  const embedTaskNode = useMemo(
    () =>
      isOwner &&
      user?.featureFlags?.shareEmbedded &&
      user?.subscription?.isPremium && (
        <EmbedTask
          taskId={id}
          isPublic={isPublic}
          summary={task?.summary?.v1}
        />
      ),
    [
      id,
      isOwner,
      isPublic,
      user?.featureFlags?.shareEmbedded,
      user?.subscription?.isPremium,
      task
    ]
  )

  const deleteTaskNode = useMemo(
    () => isOwner && <DeleteTask tracksIds={[id]} />,
    [id, isOwner]
  )

  return useMemo(
    () =>
      [
        addToPlaylistNode,
        editTaskNode,
        repeatSongNode,
        viewInstructionsNode,
        embedTaskNode,
        deleteTaskNode
      ].filter(Boolean),
    [
      addToPlaylistNode,
      viewInstructionsNode,
      deleteTaskNode,
      editTaskNode,
      embedTaskNode,
      repeatSongNode
    ]
  )
}
